import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { Plus, Heart, MessageCircle, Users, Trash2, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
    { value: 'general', label: 'General Discussion' },
    { value: 'support', label: 'Support & Encouragement' },
    { value: 'tips', label: 'Tips & Advice' },
    { value: 'questions', label: 'Questions' },
    { value: 'recipes', label: 'Recipes & Food' },
];

export default function Community() {
    const { token, user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [showNewPost, setShowNewPost] = useState(false);
    const [selectedPost, setSelectedPost] = useState(null);
    
    // New post form
    const [newTitle, setNewTitle] = useState('');
    const [newContent, setNewContent] = useState('');
    const [newCategory, setNewCategory] = useState('general');
    const [posting, setPosting] = useState(false);
    
    // Comment
    const [newComment, setNewComment] = useState('');
    const [commenting, setCommenting] = useState(false);

    const fetchPosts = useCallback(async () => {
        setLoading(true);
        try {
            const url = selectedCategory 
                ? `${API_URL}/api/community/posts?category=${selectedCategory}`
                : `${API_URL}/api/community/posts`;
            const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.ok) {
                const data = await res.json();
                setPosts(data);
            }
        } catch (error) {
            console.error('Failed to fetch posts:', error);
        } finally {
            setLoading(false);
        }
    }, [token, selectedCategory]);

    useEffect(() => {
        fetchPosts();
    }, [fetchPosts]);

    const fetchPostDetails = async (postId) => {
        try {
            const res = await fetch(`${API_URL}/api/community/posts/${postId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSelectedPost(data);
            }
        } catch (error) {
            console.error('Failed to fetch post:', error);
        }
    };

    const createPost = async () => {
        if (!newTitle.trim() || !newContent.trim()) {
            toast.error('Please fill in all fields');
            return;
        }

        setPosting(true);
        try {
            const res = await fetch(`${API_URL}/api/community/posts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ title: newTitle, content: newContent, category: newCategory })
            });

            if (res.ok) {
                toast.success('Post created!');
                setShowNewPost(false);
                setNewTitle('');
                setNewContent('');
                fetchPosts();
            }
        } catch (error) {
            toast.error('Failed to create post');
        } finally {
            setPosting(false);
        }
    };

    const addComment = async () => {
        if (!newComment.trim() || !selectedPost) return;

        setCommenting(true);
        try {
            const res = await fetch(`${API_URL}/api/community/posts/${selectedPost.id}/comments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ content: newComment })
            });

            if (res.ok) {
                setNewComment('');
                fetchPostDetails(selectedPost.id);
                fetchPosts();
            }
        } catch (error) {
            toast.error('Failed to add comment');
        } finally {
            setCommenting(false);
        }
    };

    const toggleLike = async (postId) => {
        try {
            await fetch(`${API_URL}/api/community/posts/${postId}/like`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            fetchPosts();
            if (selectedPost?.id === postId) {
                fetchPostDetails(postId);
            }
        } catch (error) {
            toast.error('Failed to like');
        }
    };

    const deletePost = async (postId) => {
        if (!window.confirm('Delete this post?')) return;
        try {
            await fetch(`${API_URL}/api/community/posts/${postId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            toast.success('Post deleted');
            setSelectedPost(null);
            fetchPosts();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-bold">Community</h1>
                    <p className="text-muted-foreground mt-1">Connect with other parents on this journey</p>
                </div>
                <Button onClick={() => setShowNewPost(true)} data-testid="new-post-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Post
                </Button>
            </div>

            {/* Category Filter */}
            <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-full">
                <TabsList className="flex-wrap h-auto gap-1">
                    <TabsTrigger value="" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">All</TabsTrigger>
                    {CATEGORIES.map(cat => (
                        <TabsTrigger key={cat.value} value={cat.value} className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                            {cat.label}
                        </TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>

            {/* New Post Dialog */}
            <Dialog open={showNewPost} onOpenChange={setShowNewPost}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Create a Post</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <Input placeholder="Title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} data-testid="post-title-input" />
                        <Textarea placeholder="Share your thoughts..." value={newContent} onChange={(e) => setNewContent(e.target.value)} rows={5} data-testid="post-content-input" />
                        <Select value={newCategory} onValueChange={setNewCategory}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {CATEGORIES.map(cat => <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Button onClick={createPost} disabled={posting} className="w-full" data-testid="submit-post-btn">
                            {posting ? 'Posting...' : 'Post'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Post Detail Dialog */}
            <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
                <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
                    {selectedPost && (
                        <>
                            <DialogHeader>
                                <DialogTitle>{selectedPost.title}</DialogTitle>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <span>{selectedPost.user_name}</span>
                                    <span>•</span>
                                    <span>{format(parseISO(selectedPost.created_at), 'MMM d, yyyy')}</span>
                                    <Badge variant="outline">{selectedPost.category}</Badge>
                                </div>
                            </DialogHeader>
                            <div className="py-4">
                                <p className="whitespace-pre-wrap">{selectedPost.content}</p>
                                <div className="flex items-center gap-4 mt-4 pt-4 border-t">
                                    <Button variant="ghost" size="sm" onClick={() => toggleLike(selectedPost.id)}>
                                        <Heart className="w-4 h-4 mr-1" />
                                        {selectedPost.likes}
                                    </Button>
                                    <span className="text-sm text-muted-foreground">{selectedPost.comment_count} comments</span>
                                    {selectedPost.user_id === user?.id && (
                                        <Button variant="ghost" size="sm" className="text-red-500 ml-auto" onClick={() => deletePost(selectedPost.id)}>
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                            {/* Comments */}
                            <div className="border-t pt-4 space-y-4">
                                <h4 className="font-semibold">Comments</h4>
                                {selectedPost.comments?.map((comment) => (
                                    <div key={comment.id} className="p-3 rounded-lg bg-muted/50">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-medium text-sm">{comment.user_name}</span>
                                            <span className="text-xs text-muted-foreground">{format(parseISO(comment.created_at), 'MMM d')}</span>
                                        </div>
                                        <p className="text-sm">{comment.content}</p>
                                    </div>
                                ))}
                                <div className="flex gap-2">
                                    <Input placeholder="Add a comment..." value={newComment} onChange={(e) => setNewComment(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addComment()} />
                                    <Button onClick={addComment} disabled={commenting}><Send className="w-4 h-4" /></Button>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Posts List */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            ) : posts.length === 0 ? (
                <Card className="text-center py-12">
                    <CardContent>
                        <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="text-muted-foreground">No posts yet. Be the first to share!</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4">
                    {posts.map((post) => (
                        <Card key={post.id} className="card-hover cursor-pointer" onClick={() => fetchPostDetails(post.id)} data-testid={`post-${post.id}`}>
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Badge variant="outline" className="text-xs">{post.category}</Badge>
                                            <span className="text-xs text-muted-foreground">{post.user_name}</span>
                                            <span className="text-xs text-muted-foreground">• {format(parseISO(post.created_at), 'MMM d')}</span>
                                        </div>
                                        <h3 className="font-semibold mb-2">{post.title}</h3>
                                        <p className="text-sm text-muted-foreground line-clamp-2">{post.content}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4 mt-4 pt-4 border-t">
                                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                        <Heart className="w-4 h-4" />
                                        {post.likes}
                                    </div>
                                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                        <MessageCircle className="w-4 h-4" />
                                        {post.comment_count}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
